/**
 * @fileoverview Fail-closed guard for documents whose PDF fill failed
 * (MOR-2342). A mapped form that could not be filled — typically a value no
 * legible size could contain — carries `pdfFillError` and has NO current
 * filled PDF. Nothing may download, print, bundle or attach it until the
 * mapping is fixed and it is regenerated: not the blank source PDF, not a
 * PDF from an earlier successful fill, not a letter-style render of it.
 */

export interface FillGuardedDoc {
  _id: string;
  name: string;
  pdfFillError?: string | null;
}

/** The fill error blocking this document, or null when it can be exported. */
export function fillBlockReason(doc: {
  pdfFillError?: string | null;
}): string | null {
  const reason = doc.pdfFillError?.trim();
  return reason ? reason : null;
}

/** Split documents into exportable ones and those blocked by a fill error. */
export function partitionByFillError<T extends FillGuardedDoc>(
  docs: ReadonlyArray<T>,
): { ready: T[]; blocked: Array<{ doc: T; reason: string }> } {
  return docs.reduce<{
    ready: T[];
    blocked: Array<{ doc: T; reason: string }>;
  }>(
    (acc, doc) => {
      const reason = fillBlockReason(doc);
      return reason
        ? { ...acc, blocked: [...acc.blocked, { doc, reason }] }
        : { ...acc, ready: [...acc.ready, doc] };
    },
    { ready: [], blocked: [] },
  );
}

/** One toast-sized sentence naming the blocked documents and the first reason. */
export function describeBlockedDownloads(
  blocked: ReadonlyArray<{ doc: { name: string }; reason: string }>,
): string {
  if (blocked.length === 0) return "";
  const names = blocked.map((b) => `"${b.doc.name}"`).join(", ");
  const verb = blocked.length === 1 ? "was" : "were";
  return `${names} ${verb} skipped: ${blocked[0].reason}`;
}
